<div class="air-container">
  <div class="air-header">
    <h2>Annual / Donor Impact Report Builder</h2>
    <p class="air-subtitle">Generate an AI-powered, print-ready impact report for a CSR partner</p>
  </div>

  <mat-stepper linear #stepper class="air-stepper" (selectionChange)="onStepChange($event)">

    <!-- ═══════════════════════════════════════════════════════
         STEP 1 — Configuration
    ═══════════════════════════════════════════════════════ -->
    <mat-step [stepControl]="configForm" label="Configuration">
      <form [formGroup]="configForm" class="step-form">
        <h3 class="step-title">Report Configuration</h3>
        <div class="help-note">
          <mat-icon>info</mat-icon>
          <ul>
            <li>Select the <strong>centre</strong> and <strong>financial year</strong> first — every later step (statistics, galleries, department reports, case studies) uses them.</li>
            <li>The <strong>Donor / CSR Partner</strong> name is used in the AI narratives and the volunteer-event introduction; leave it blank to credit Access Life.</li>
            <li>The cover title fills in from the financial year; edit it if needed. Pick the cover and contact page photos, and the placeholder images used to fill blank space in the report.</li>
          </ul>
        </div>

        <mat-form-field appearance="outline" class="field-full">
          <mat-label>Centre</mat-label>
          <mat-select formControlName="centreCode" (selectionChange)="onCentreChange($event)">
            <mat-option *ngFor="let c of (centres$ | async)" [value]="c.centreCode">
              {{c.centreName}} ({{c.centreCode}})
            </mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline" class="field-full">
          <mat-label>Financial Year</mat-label>
          <mat-select formControlName="financialYear">
            <mat-option *ngFor="let fy of financialYears" [value]="fy.label">
              {{fy.label}}
            </mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline" class="field-full">
          <mat-label>Donor / CSR Partner Name (optional)</mat-label>
          <input matInput formControlName="donorName" placeholder="e.g. L&T, Tata Consultancy Services">
        </mat-form-field>

        <mat-form-field appearance="outline" class="field-full">
          <mat-label>Cover Title</mat-label>
          <input matInput formControlName="coverTitle" placeholder="e.g. ANNUAL REPORT 2026-27">
        </mat-form-field>

        <mat-form-field appearance="outline" class="field-full">
          <mat-label>Watermark Style</mat-label>
          <mat-select formControlName="watermarkStyle">
            <mat-option value="wave">Wave</mat-option>
            <mat-option value="circle">Circle</mat-option>
            <mat-option value="none">None</mat-option>
          </mat-select>
        </mat-form-field>

        <div class="photo-upload-section" *ngIf="(coverPhotoLibrary$ | async) as libraryPhotos">
          <p class="upload-label">Cover Photo (front page)</p>
          <p class="photo-note">
            <mat-icon>touch_app</mat-icon>
            <span>Click a photo to select it — the <strong>highlighted</strong> photo is the one shown on the report’s cover page. Or upload a new one.
            Use ✕ to delete a photo from the library. Shared with the Narrative Impact Report.</span>
          </p>
          <p class="photo-note">
            <mat-icon>photo_size_select_large</mat-icon>
            <span><strong>Best photo:</strong> portrait (about 2:3), <strong>1200 × 1750 px</strong> (at least 600 × 900 px),
            a cut-out PNG with a transparent background, under ~3 MB. It is shown bottom-right on the cover at up
            to 178 × 148 mm, never cropped; keep little empty space around the people.</span>
          </p>
          <div class="child-photo-gallery" *ngIf="libraryPhotos.length">
            <div
              *ngFor="let p of libraryPhotos"
              class="child-photo-gallery-thumb"
              [class.is-selected]="p.photoUrl === coverPhotoUrl"
              [title]="$any(p).childName || ''"
              (click)="coverPhotoUrl = p.photoUrl">
              <img [src]="p.photoUrl" alt="cover">
              <span class="thumb-caption" *ngIf="$any(p).childName">{{$any(p).childName}}</span>
              <button mat-icon-button class="thumb-remove" type="button" title="Delete from library"
                      (click)="$event.stopPropagation(); removeCoverPhoto($any(p))">
                <mat-icon>close</mat-icon>
              </button>
            </div>
          </div>
          <input type="file" accept="image/*" (change)="uploadCoverPhoto($event)" #coverInput hidden>
          <button mat-stroked-button color="accent" type="button" (click)="coverInput.click()" [disabled]="uploadingCoverPhoto">
            <mat-icon>{{uploadingCoverPhoto ? 'hourglass_empty' : 'upload_file'}}</mat-icon>
            {{uploadingCoverPhoto ? 'Uploading…' : 'Upload Cover Photo'}}
          </button>
          <div class="photo-preview-row" *ngIf="coverPhotoUrl && !isPhotoInLibrary(libraryPhotos, coverPhotoUrl)">
            <div class="photo-thumb"><img [src]="coverPhotoUrl" alt="cover"></div>
          </div>
        </div>

        <div class="photo-upload-section" *ngIf="(contactPhotoLibrary$ | async) as libraryPhotos">
          <p class="upload-label">Contact Page Photo</p>
          <p class="photo-note">
            <mat-icon>touch_app</mat-icon>
            <span>Click a photo to select it — the <strong>highlighted</strong> photo is shown on the right side of the last (Contact) page. Or upload a new one.
            Use ✕ to delete a photo from the library. Shared with the Narrative Impact Report.</span>
          </p>
          <p class="photo-note">
            <mat-icon>photo_size_select_large</mat-icon>
            <span><strong>Best photo:</strong> portrait (about 3:4), <strong>1400 × 1900 px</strong> (at least 700 × 950 px),
            under ~3 MB. A cut-out PNG with a transparent background stands in the yellow band (up to 121 × 188 mm);
            any other photo is framed beside the contact details, above the band (up to 108 × 150 mm). Photos are
            never cropped and never cover the contact text; a landscape photo comes out smaller.</span>
          </p>
          <div class="child-photo-gallery" *ngIf="libraryPhotos.length">
            <div
              *ngFor="let p of libraryPhotos"
              class="child-photo-gallery-thumb"
              [class.is-selected]="p.photoUrl === contactPhotoUrl"
              [title]="$any(p).childName || ''"
              (click)="contactPhotoUrl = p.photoUrl">
              <img [src]="p.photoUrl" alt="contact">
              <button mat-icon-button class="thumb-remove" type="button" title="Delete from library"
                      (click)="$event.stopPropagation(); removeContactPhoto($any(p))">
                <mat-icon>close</mat-icon>
              </button>
            </div>
          </div>
          <input type="file" accept="image/*" (change)="uploadContactPhoto($event)" #contactInput hidden>
          <button mat-stroked-button color="accent" type="button" (click)="contactInput.click()" [disabled]="uploadingContactPhoto">
            <mat-icon>{{uploadingContactPhoto ? 'hourglass_empty' : 'upload_file'}}</mat-icon>
            {{uploadingContactPhoto ? 'Uploading…' : 'Upload Contact Photo'}}
          </button>
          <div class="photo-preview-row" *ngIf="contactPhotoUrl && !isPhotoInLibrary(libraryPhotos, contactPhotoUrl)">
            <div class="photo-thumb"><img [src]="contactPhotoUrl" alt="contact"></div>
          </div>
        </div>

        <div class="photo-upload-section" *ngIf="(placeholderLibrary$ | async) as placeholderLibrary">
          <p class="upload-label">Placeholder Images</p>
          <p class="photo-note">
            <mat-icon>touch_app</mat-icon>
            <span>Used to fill the blank space at the bottom of report pages (only where less than 60% of the
            page is empty). This is the same library as the Narrative Impact Report. Each image is used at
            most once per report — once they run out, the remaining gaps stay blank — so select plenty,
            ideally a mix of shapes.
            Click an image to select or unselect it; use ✕ to delete it from the library.</span>
          </p>
          <p class="photo-count" *ngIf="placeholderLibrary.length">
            <strong>{{selectedPlaceholderPhotos.length}} of {{placeholderLibrary.length}} selected.</strong>
            <span *ngIf="selectedPlaceholderPhotos.length === 0">
              None selected — blank space in the report will be left empty.
            </span>
          </p>
          <div class="child-photo-gallery" *ngIf="placeholderLibrary.length">
            <div
              *ngFor="let ph of placeholderLibrary"
              class="child-photo-gallery-thumb"
              [class.is-selected]="selectedPlaceholderPhotos.includes(ph.photoUrl)"
              (click)="togglePlaceholderPhoto(ph.photoUrl)">
              <img [src]="ph.photoUrl" alt="placeholder image">
              <button mat-icon-button class="thumb-remove" type="button" title="Delete from library"
                      (click)="$event.stopPropagation(); removePlaceholderPhoto(ph)">
                <mat-icon>close</mat-icon>
              </button>
            </div>
          </div>
          <input type="file" accept="image/*" multiple (change)="uploadPlaceholderPhotos($event)" #placeholderInput hidden>
          <button mat-stroked-button color="accent" type="button" (click)="placeholderInput.click()" [disabled]="uploadingPlaceholder">
            <mat-icon>{{uploadingPlaceholder ? 'hourglass_empty' : 'upload_file'}}</mat-icon>
            {{uploadingPlaceholder ? 'Uploading…' : 'Upload Placeholder Images'}}
          </button>
        </div>

        <div class="step-actions">
          <button mat-raised-button color="primary" matStepperNext [disabled]="configForm.invalid">
            Next: Fetch Stats <mat-icon>arrow_forward</mat-icon>
          </button>
        </div>
      </form>
    </mat-step>

    <!-- ═══════════════════════════════════════════════════════
         STEP 2 — Statistics
    ═══════════════════════════════════════════════════════ -->
    <mat-step [stepControl]="statsForm" label="Statistics">
      <form [formGroup]="statsForm">
        <h3 class="step-title">Statistics for {{period}}</h3>
        <div class="help-note">
          <mat-icon>info</mat-icon>
          <ul>
            <li>Admission, discharge and session counts for the centre and year are filled in automatically when you open this step, along with the list of discharged children for case studies.</li>
            <li>Correct any number by editing it. The cumulative figures without a data source (children sheltered, stress-free nights, vehicles, kms) must be entered by hand.</li>
          </ul>
        </div>

        <div class="fetch-status" *ngIf="fetchingStats">
          <mat-progress-bar mode="indeterminate"></mat-progress-bar>
          <span>Fetching data for the selected centre and period…</span>
        </div>

        <!-- Admission Stats -->
        <mat-card class="stats-card">
          <mat-card-title>Child Admission Statistics</mat-card-title>
          <mat-card-subtitle>Auto-fetched from ALChild · Edit if needed</mat-card-subtitle>
          <mat-card-content>
            <div class="stats-grid">
              <mat-form-field appearance="outline">
                <mat-label>Admissions</mat-label>
                <input matInput type="number" formControlName="admission">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Discharges</mat-label>
                <input matInput type="number" formControlName="discharge">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Follow-up Admissions</mat-label>
                <input matInput type="number" formControlName="followUpAdmission">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Follow-up Discharges</mat-label>
                <input matInput type="number" formControlName="followUpDischarge">
              </mat-form-field>
            </div>
          </mat-card-content>
        </mat-card>

        <!-- Education Stats -->
        <mat-card class="stats-card">
          <mat-card-title>Education Session Counts</mat-card-title>
          <mat-card-subtitle>Auto-fetched from educationMonthlySummary</mat-card-subtitle>
          <mat-card-content>
            <div class="stats-grid">
              <mat-form-field appearance="outline">
                <mat-label>Individual Sessions</mat-label>
                <input matInput type="number" formControlName="eduIndividual">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Group Sessions</mat-label>
                <input matInput type="number" formControlName="eduGroup">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Total (auto)</mat-label>
                <input matInput readonly
                  [value]="(statsForm.value.eduIndividual || 0) + (statsForm.value.eduGroup || 0)">
              </mat-form-field>
            </div>
          </mat-card-content>
        </mat-card>

        <!-- Counselling Stats -->
        <mat-card class="stats-card">
          <mat-card-title>Counselling Session Counts</mat-card-title>
          <mat-card-subtitle>Auto-fetched from counsellingMonthlySummary</mat-card-subtitle>
          <mat-card-content>
            <div class="stats-grid">
              <mat-form-field appearance="outline">
                <mat-label>Individual</mat-label>
                <input matInput type="number" formControlName="cslIndividual">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Group</mat-label>
                <input matInput type="number" formControlName="cslGroup">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Assessment</mat-label>
                <input matInput type="number" formControlName="cslAssessment">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Total (auto)</mat-label>
                <input matInput readonly
                  [value]="(statsForm.value.cslIndividual||0)+(statsForm.value.cslGroup||0)+(statsForm.value.cslAssessment||0)">
              </mat-form-field>
            </div>
          </mat-card-content>
        </mat-card>

        <!-- Cumulative Stats (Manual) -->
        <mat-card class="stats-card manual-card">
          <mat-card-title>
            <mat-icon>edit</mat-icon> Cumulative Impact Stats
          </mat-card-title>
          <mat-card-subtitle>Shown at top of report — enter manually</mat-card-subtitle>
          <mat-card-content>
            <div class="stats-grid">
              <mat-form-field appearance="outline">
                <mat-label>Centres Operational</mat-label>
                <input matInput type="number" formControlName="centres">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Children Sheltered (since 2014)</mat-label>
                <input matInput type="number" formControlName="childrenSheltered">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Total Education Sessions (all-time)</mat-label>
                <input matInput type="number" formControlName="totalEduSessions">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Total Counselling Sessions (all-time)</mat-label>
                <input matInput type="number" formControlName="totalCslSessions">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Stress-Free Nights</mat-label>
                <input matInput type="number" formControlName="stressFreeNights">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Hospital Transit Vehicles</mat-label>
                <input matInput type="number" formControlName="transitVehicles">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Total Kms</mat-label>
                <input matInput type="number" formControlName="totalKms">
              </mat-form-field>
            </div>
          </mat-card-content>
        </mat-card>

        <div class="step-actions">
          <button mat-button matStepperPrevious>Back</button>
          <button mat-raised-button color="primary" matStepperNext [disabled]="statsForm.invalid">
            Next: Generate Narratives <mat-icon>auto_awesome</mat-icon>
          </button>
        </div>
      </form>
    </mat-step>

    <!-- ═══════════════════════════════════════════════════════
         STEP 3 — AI Narratives
    ═══════════════════════════════════════════════════════ -->
    <mat-step label="AI Narratives">
      <h3 class="step-title">AI-Generated Narratives</h3>
      <div class="help-note">
        <mat-icon>info</mat-icon>
        <ul>
          <li>Optionally click <strong>Fetch Education &amp; Counselling Reports</strong> first: the teams’ own highlights are given to the AI and their photos are added to the galleries.</li>
          <li>Click <strong>Generate All 4 Narratives at Once</strong>, or the refresh button on one section, then review and edit the text in each editor.</li>
          <li>Under Counselling and Teaching, pick photos from the gallery or upload them; each set gets its own gallery page. Optionally build a collage from 4–8 of them.</li>
        </ul>
      </div>

      <mat-card class="narrative-card">
        <mat-card-title><mat-icon>history_edu</mat-icon> Existing Education &amp; Counselling Impact Reports</mat-card-title>
        <mat-card-content>
          <p class="photo-count">
            Loads the Education and Counselling teams' saved impact reports for this centre and financial
            year. Their highlights go into the boxes below and are given to the AI when it writes the
            Teaching and Counselling narratives; their photos are added to the galleries.
          </p>
          <button mat-raised-button color="accent" type="button" (click)="fetchDepartmentReports()" [disabled]="fetchingDeptReports">
            <mat-icon>{{fetchingDeptReports ? 'hourglass_empty' : 'cloud_download'}}</mat-icon>
            {{fetchingDeptReports ? 'Fetching...' : 'Fetch Education & Counselling Reports'}}
          </button>
          <mat-form-field appearance="outline" class="field-full mt-8">
            <mat-label>Education highlights for the AI (one per line)</mat-label>
            <textarea matInput rows="4" [(ngModel)]="teachingContext"
              placeholder="Filled from the Education team's reports, or type your own"></textarea>
          </mat-form-field>
          <mat-form-field appearance="outline" class="field-full">
            <mat-label>Counselling highlights for the AI (one per line)</mat-label>
            <textarea matInput rows="4" [(ngModel)]="counsellingContext"
              placeholder="Filled from the Counselling team's reports, or type your own"></textarea>
          </mat-form-field>
        </mat-card-content>
      </mat-card>

      <div class="generate-all-bar">
        <button mat-raised-button color="primary"
          (click)="generateAllNarratives()"
          [disabled]="generatingNutrition || generatingTransport || generatingCounselling || generatingTeaching">
          <mat-icon>auto_awesome</mat-icon>
          Generate All 4 Narratives at Once
        </button>
      </div>

      <!-- Nutrition -->
      <mat-card class="narrative-card">
        <mat-card-title>
          <mat-icon>restaurant</mat-icon> Nutrition Narrative
          <button mat-icon-button color="accent" (click)="generateSingleNarrative('nutrition')"
            [disabled]="generatingNutrition" matTooltip="Re-generate">
            <mat-icon>{{generatingNutrition ? 'hourglass_empty' : 'refresh'}}</mat-icon>
          </button>
        </mat-card-title>
        <mat-card-content>
          <mat-progress-bar *ngIf="generatingNutrition" mode="indeterminate"></mat-progress-bar>
          <ckeditor [editor]="Editor" [(ngModel)]="nutritionNarrative" *ngIf="!generatingNutrition || nutritionNarrative"></ckeditor>
        </mat-card-content>
      </mat-card>

      <!-- Transport -->
      <mat-card class="narrative-card">
        <mat-card-title>
          <mat-icon>directions_bus</mat-icon> Transportation Narrative
          <button mat-icon-button color="accent" (click)="generateSingleNarrative('transport')"
            [disabled]="generatingTransport" matTooltip="Re-generate">
            <mat-icon>{{generatingTransport ? 'hourglass_empty' : 'refresh'}}</mat-icon>
          </button>
        </mat-card-title>
        <mat-card-content>
          <mat-progress-bar *ngIf="generatingTransport" mode="indeterminate"></mat-progress-bar>
          <ckeditor [editor]="Editor" [(ngModel)]="transportNarrative" *ngIf="!generatingTransport || transportNarrative"></ckeditor>
        </mat-card-content>
      </mat-card>

      <!-- Counselling -->
      <mat-card class="narrative-card">
        <mat-card-title>
          <mat-icon>psychology</mat-icon> Counselling Highlights
          <button mat-icon-button color="accent" (click)="generateSingleNarrative('counselling')"
            [disabled]="generatingCounselling" matTooltip="Re-generate">
            <mat-icon>{{generatingCounselling ? 'hourglass_empty' : 'refresh'}}</mat-icon>
          </button>
        </mat-card-title>
        <mat-card-content>
          <mat-progress-bar *ngIf="generatingCounselling" mode="indeterminate"></mat-progress-bar>
          <ckeditor [editor]="Editor" [(ngModel)]="counsellingNarrative" *ngIf="!generatingCounselling || counsellingNarrative"></ckeditor>
          <div class="photo-upload-section">
            <p class="upload-label">Counselling Section Photos</p>
            <p class="photo-note">
              <mat-icon>dashboard_customize</mat-icon>
              <span>All selected photos are shown on their own gallery page. For a good collage, select
              <strong>minimum 4 to 8 images</strong>, then click <strong>Generate Collage</strong> (optional).</span>
            </p>
            <button mat-stroked-button color="primary" (click)="openCounsellingGallery()">
              <mat-icon>photo_library</mat-icon> Pick from Counselling Gallery
            </button>
            <input type="file" multiple accept="image/*"
              (change)="uploadCounsellingPhotosFromDevice($event)" #cslPhotoInput hidden>
            <button mat-stroked-button color="accent" (click)="cslPhotoInput.click()"
              [disabled]="uploadingCounsellingPhotos">
              <mat-icon>{{uploadingCounsellingPhotos ? 'hourglass_empty' : 'upload_file'}}</mat-icon>
              {{uploadingCounsellingPhotos ? 'Uploading…' : 'Upload from Device'}}
            </button>
            <button mat-stroked-button color="primary" [disabled]="counsellingPhotoUrls.length < 2 || generatingCounsellingCollage"
              (click)="buildCounsellingCollage()">
              <mat-icon>{{generatingCounsellingCollage ? 'hourglass_empty' : 'dashboard_customize'}}</mat-icon>
              {{generatingCounsellingCollage ? 'Saving…' : (counsellingCollageUrl ? 'Regenerate' : 'Generate') + ' Collage'}}
            </button>
            <span class="photo-count" *ngIf="counsellingPhotoUrls.length">
              {{counsellingPhotoUrls.length}} photo(s) selected
            </span>
            <div class="photo-thumb collage-preview" *ngIf="counsellingCollageUrl">
              <img [src]="counsellingCollageUrl" alt="counselling collage preview">
              <span class="collage-badge">Collage</span>
            </div>
            <div class="photo-preview-row">
              <div *ngFor="let url of counsellingPhotoUrls; let i = index" class="photo-thumb">
                <img [src]="url" alt="counselling photo">
                <button mat-icon-button color="warn" class="remove-photo" (click)="removeCounsellingPhoto(i)">
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            </div>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- Teaching -->
      <mat-card class="narrative-card">
        <mat-card-title>
          <mat-icon>school</mat-icon> Teaching Highlights
          <button mat-icon-button color="accent" (click)="generateSingleNarrative('teaching')"
            [disabled]="generatingTeaching" matTooltip="Re-generate">
            <mat-icon>{{generatingTeaching ? 'hourglass_empty' : 'refresh'}}</mat-icon>
          </button>
        </mat-card-title>
        <mat-card-content>
          <mat-progress-bar *ngIf="generatingTeaching" mode="indeterminate"></mat-progress-bar>
          <ckeditor [editor]="Editor" [(ngModel)]="teachingNarrative" *ngIf="!generatingTeaching || teachingNarrative"></ckeditor>
          <div class="photo-upload-section">
            <p class="upload-label">Teaching Section Photos</p>
            <p class="photo-note">
              <mat-icon>dashboard_customize</mat-icon>
              <span>All selected photos are shown on their own gallery page. For a good collage, select
              <strong>minimum 4 to 8 images</strong>, then click <strong>Generate Collage</strong> (optional).</span>
            </p>
            <button mat-stroked-button color="primary" (click)="openTeachingGallery()">
              <mat-icon>photo_library</mat-icon> Pick from Education Gallery
            </button>
            <input type="file" multiple accept="image/*"
              (change)="uploadTeachingPhotosFromDevice($event)" #eduPhotoInput hidden>
            <button mat-stroked-button color="accent" (click)="eduPhotoInput.click()"
              [disabled]="uploadingTeachingPhotos">
              <mat-icon>{{uploadingTeachingPhotos ? 'hourglass_empty' : 'upload_file'}}</mat-icon>
              {{uploadingTeachingPhotos ? 'Uploading…' : 'Upload from Device'}}
            </button>
            <button mat-stroked-button color="primary" [disabled]="teachingPhotoUrls.length < 2 || generatingTeachingCollage"
              (click)="buildTeachingCollage()">
              <mat-icon>{{generatingTeachingCollage ? 'hourglass_empty' : 'dashboard_customize'}}</mat-icon>
              {{generatingTeachingCollage ? 'Saving…' : (teachingCollageUrl ? 'Regenerate' : 'Generate') + ' Collage'}}
            </button>
            <span class="photo-count" *ngIf="teachingPhotoUrls.length">
              {{teachingPhotoUrls.length}} photo(s) selected
            </span>
            <div class="photo-thumb collage-preview" *ngIf="teachingCollageUrl">
              <img [src]="teachingCollageUrl" alt="teaching collage preview">
              <span class="collage-badge">Collage</span>
            </div>
            <div class="photo-preview-row">
              <div *ngFor="let url of teachingPhotoUrls; let i = index" class="photo-thumb">
                <img [src]="url" alt="teaching photo">
                <button mat-icon-button color="warn" class="remove-photo" (click)="removeTeachingPhoto(i)">
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            </div>
          </div>
        </mat-card-content>
      </mat-card>

      <div class="step-actions">
        <button mat-button matStepperPrevious>Back</button>
        <button mat-raised-button color="primary" matStepperNext>
          Next: Case Studies <mat-icon>person</mat-icon>
        </button>
      </div>
    </mat-step>

    <!-- ═══════════════════════════════════════════════════════
         STEP 4 — Case Studies
    ═══════════════════════════════════════════════════════ -->
    <mat-step label="Case Studies">
      <h3 class="step-title">Happy Discharge Case Studies</h3>
      <div class="help-note">
        <mat-icon>info</mat-icon>
        <ul>
          <li>Tick each discharged child to feature; every selected child gets their own case-study page.</li>
          <li>Write short <strong>Instructions for AI Generation</strong> (education and counselling), then generate the story, quotes and highlight bullets — all editable afterwards.</li>
          <li>Add photos: the first appears beside the child’s details, the rest as a collage below the story.</li>
        </ul>
      </div>

      <p class="step-desc" *ngIf="dischargedChildren.length === 0">
        No discharged children found for this period. Fetch stats in Step 2 first, or the period had no discharges.
      </p>

      <!-- Select children -->
      <div *ngIf="dischargedChildren.length > 0">
        <p class="step-desc">Select which children to feature as case studies:</p>
        <div class="child-selection-grid">
          <mat-card *ngFor="let child of dischargedChildren" class="child-card"
            [class.selected]="child.selected" (click)="child.selected = !child.selected">
            <mat-checkbox [(ngModel)]="child.selected" (click)="$event.stopPropagation()"></mat-checkbox>
            <div class="child-info">
              <strong>{{child.name}}</strong>
              <span>{{child.age}}y · {{child.gender}} · {{child.city}}, {{child.state}}</span>
              <span class="diagnosis">{{child.diagnosis}}</span>
              <span class="stay">Stay: {{child.stayDuration}}</span>
            </div>
          </mat-card>
        </div>
      </div>

      <!-- Per-selected child form -->
      <div *ngFor="let child of selectedChildren" class="case-study-form">
        <mat-card class="cs-card">
          <mat-card-title>{{child.name}} — Case Study</mat-card-title>
          <mat-card-subtitle>Discharge: {{child.dischargeDate | date:'d MMM yyyy'}} · Stay: {{child.stayDuration}}</mat-card-subtitle>
          <mat-card-content>

            <!-- Child profile (editable) -->
            <div class="cs-profile-grid">
              <mat-form-field appearance="outline">
                <mat-label>Name</mat-label>
                <input matInput [(ngModel)]="child.name">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Gender</mat-label>
                <input matInput [(ngModel)]="child.gender">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Age</mat-label>
                <input matInput type="number" [(ngModel)]="child.age">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>DOB</mat-label>
                <input matInput [(ngModel)]="child.dob">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>City</mat-label>
                <input matInput [(ngModel)]="child.city">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>State</mat-label>
                <input matInput [(ngModel)]="child.state">
              </mat-form-field>
              <mat-form-field appearance="outline" class="field-full">
                <mat-label>Diagnosis</mat-label>
                <input matInput [(ngModel)]="child.diagnosis">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Hospital</mat-label>
                <input matInput [(ngModel)]="child.hospital">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Stay Duration</mat-label>
                <input matInput [(ngModel)]="child.stayDuration">
              </mat-form-field>
            </div>

            <!-- Highlights context for AI -->
            <mat-form-field appearance="outline" class="field-full">
              <mat-label>Education – Instructions for AI Generation</mat-label>
              <textarea matInput rows="3" [(ngModel)]="child.educationHighlights"
                placeholder="e.g. Strong interest in maths, actively participated in science experiments..."></textarea>
            </mat-form-field>
            <mat-form-field appearance="outline" class="field-full">
              <mat-label>Counselling – Instructions for AI Generation</mat-label>
              <textarea matInput rows="3" [(ngModel)]="child.counsellingHighlights"
                placeholder="e.g. Initially anxious, significant progress through CBT, family showed improved coping..."></textarea>
            </mat-form-field>

            <!-- Generate Story -->
            <button mat-raised-button color="accent" (click)="generateCaseStory(child)"
              [disabled]="child.generating">
              <mat-icon>{{child.generating ? 'hourglass_empty' : 'auto_awesome'}}</mat-icon>
              {{child.generating ? 'Generating...' : 'Generate AI Story'}}
            </button>

            <!-- Story editor -->
            <div *ngIf="child.story" class="story-editor-section">
              <mat-form-field appearance="outline" class="field-full">
                <mat-label>Opening Quote</mat-label>
                <input matInput [(ngModel)]="child.openingQuote">
              </mat-form-field>
              <ckeditor [editor]="Editor" [(ngModel)]="child.story"></ckeditor>
              <mat-form-field appearance="outline" class="field-full mt-8">
                <mat-label>Closing Quote</mat-label>
                <input matInput [(ngModel)]="child.closingQuote">
              </mat-form-field>

              <p class="upload-label mt-8">Teaching Highlights (bullets)</p>
              <div *ngFor="let bullet of child.teachingBullets; let bi = index" class="bullet-row">
                <mat-form-field appearance="outline" class="field-full">
                  <input matInput [(ngModel)]="child.teachingBullets[bi]">
                </mat-form-field>
              </div>

              <p class="upload-label">Counselling Highlights (bullets)</p>
              <div *ngFor="let bullet of child.counsellingBullets; let bi = index" class="bullet-row">
                <mat-form-field appearance="outline" class="field-full">
                  <input matInput [(ngModel)]="child.counsellingBullets[bi]">
                </mat-form-field>
              </div>
            </div>

            <!-- Photo picker -->
            <div class="photo-upload-section">
              <p class="upload-label">Discharge / Case Study Photos</p>
              <p class="photo-note">
                <mat-icon>dashboard_customize</mat-icon>
                <span>The first photo appears beside the child’s details; the rest are shown as a collage. For a good collage, select
                <strong>minimum 4 to 8 images</strong>, then click <strong>Generate Collage</strong> (optional).</span>
              </p>
              <button mat-stroked-button color="primary" (click)="openCaseStudyGallery(child)">
                <mat-icon>photo_library</mat-icon> Pick from Education Gallery
              </button>
              <input type="file" multiple accept="image/*"
                (change)="uploadCaseStudyPhotosFromDevice($event, child)" #csPhotoInput hidden>
              <button mat-stroked-button color="accent" (click)="csPhotoInput.click()"
                [disabled]="child.uploadingPhotos">
                <mat-icon>{{child.uploadingPhotos ? 'hourglass_empty' : 'upload_file'}}</mat-icon>
                {{child.uploadingPhotos ? 'Uploading…' : 'Upload from Device'}}
              </button>
              <button mat-stroked-button color="primary" [disabled]="(child.photos?.length || 0) < 3 || child.generatingCollage"
                (click)="buildCaseStudyCollage(child)">
                <mat-icon>{{child.generatingCollage ? 'hourglass_empty' : 'dashboard_customize'}}</mat-icon>
                {{child.generatingCollage ? 'Saving…' : (child.collageUrl ? 'Regenerate' : 'Generate') + ' Collage'}}
              </button>
              <span class="photo-count" *ngIf="child.photos?.length">
                {{child.photos.length}} photo(s) selected
              </span>
              <div class="photo-thumb collage-preview" *ngIf="child.collageUrl">
                <img [src]="child.collageUrl" alt="case study collage preview">
                <span class="collage-badge">Collage</span>
              </div>
              <div class="photo-preview-row">
                <div *ngFor="let url of child.photos; let pi = index" class="photo-thumb">
                  <img [src]="url" alt="discharge photo">
                  <button mat-icon-button color="warn" class="remove-photo" (click)="removeCaseStudyPhoto(child, pi)">
                    <mat-icon>close</mat-icon>
                  </button>
                </div>
              </div>
            </div>

          </mat-card-content>
        </mat-card>
      </div>

      <div class="step-actions">
        <button mat-button matStepperPrevious>Back</button>
        <button mat-raised-button color="primary" matStepperNext>
          Next: Volunteer Events <mat-icon>groups</mat-icon>
        </button>
      </div>
    </mat-step>

    <!-- ═══════════════════════════════════════════════════════
         STEP 5 — Volunteer Events
    ═══════════════════════════════════════════════════════ -->
    <mat-step label="Volunteer Events">
      <h3 class="step-title">Volunteer / CSR Events</h3>
      <div class="help-note">
        <mat-icon>info</mat-icon>
        <ul>
          <li>Click <strong>Add Event</strong> for each volunteer or CSR visit; each event gets its own page.</li>
          <li>Enter the title, date, location and activities, then generate the Outcome, Impact and Conclusion text (editable).</li>
          <li>Upload the event photos — they are saved as soon as they’re picked.</li>
        </ul>
      </div>

      <button mat-raised-button color="accent" (click)="addEvent()">
        <mat-icon>add</mat-icon> Add Event
      </button>

      <div *ngFor="let ev of events; let ei = index" class="event-form-card">
        <mat-card>
          <mat-card-title>
            Event {{ei + 1}}
            <button mat-icon-button color="warn" (click)="removeEvent(ei)">
              <mat-icon>delete</mat-icon>
            </button>
          </mat-card-title>
          <mat-card-content>

            <div class="event-meta-grid">
              <mat-form-field appearance="outline" class="field-full">
                <mat-label>Event Title</mat-label>
                <input matInput [(ngModel)]="ev.title"
                  placeholder="e.g. L&T Volunteer Engagement Program – 8th June, 2025">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Date</mat-label>
                <input matInput [(ngModel)]="ev.date" placeholder="e.g. 8th June, 2025">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Location</mat-label>
                <input matInput [(ngModel)]="ev.location">
              </mat-form-field>
            </div>

            <p class="upload-label">Activities Conducted</p>
            <div *ngFor="let act of ev.activities; let ai = index" class="activity-row">
              <mat-form-field appearance="outline" class="act-heading">
                <mat-label>Activity Heading</mat-label>
                <input matInput [(ngModel)]="act.heading" placeholder="e.g. Interactive Games and Mask Making">
              </mat-form-field>
              <mat-form-field appearance="outline" class="act-desc">
                <mat-label>Description</mat-label>
                <textarea matInput rows="2" [(ngModel)]="act.description"></textarea>
              </mat-form-field>
              <button mat-icon-button color="warn" (click)="removeActivity(ev, ai)" *ngIf="ev.activities.length > 1">
                <mat-icon>remove_circle</mat-icon>
              </button>
            </div>
            <button mat-button (click)="addActivity(ev)">
              <mat-icon>add</mat-icon> Add Activity
            </button>

            <br>
            <button mat-raised-button color="accent" (click)="generateEventSummary(ev)"
              [disabled]="ev.generating">
              <mat-icon>{{ev.generating ? 'hourglass_empty' : 'auto_awesome'}}</mat-icon>
              {{ev.generating ? 'Generating...' : 'Generate AI Summary'}}
            </button>

            <div *ngIf="ev.outcome" class="event-summary-section">
              <mat-form-field appearance="outline" class="field-full mt-8">
                <mat-label>Outcome</mat-label>
                <textarea matInput rows="3" [(ngModel)]="ev.outcome"></textarea>
              </mat-form-field>
              <mat-form-field appearance="outline" class="field-full">
                <mat-label>Impact on Beneficiaries</mat-label>
                <textarea matInput rows="4" [(ngModel)]="ev.impact"></textarea>
              </mat-form-field>
              <mat-form-field appearance="outline" class="field-full">
                <mat-label>Conclusion</mat-label>
                <textarea matInput rows="3" [(ngModel)]="ev.conclusion"></textarea>
              </mat-form-field>
            </div>

            <!-- Event photos -->
            <div class="photo-upload-section">
              <p class="upload-label">Event Photos</p>
              <input type="file" multiple accept="image/*"
                (change)="onEventPhotoSelected($event, ev)" #evPhotoInput hidden>
              <button mat-stroked-button (click)="evPhotoInput.click()" [disabled]="ev.uploadingPhotos">
                <mat-icon>{{ev.uploadingPhotos ? 'hourglass_empty' : 'add_photo_alternate'}}</mat-icon>
                {{ev.uploadingPhotos ? 'Uploading…' : 'Upload Photos'}}
              </button>
              <span class="photo-count" *ngIf="ev.photos.length">{{ev.photos.length}} photo(s)</span>
              <div class="photo-preview-row">
                <div *ngFor="let url of ev.photos; let pi = index" class="photo-thumb">
                  <img [src]="url" alt="event photo">
                  <button mat-icon-button color="warn" class="remove-photo" (click)="removeEventPhoto(ev, pi)">
                    <mat-icon>close</mat-icon>
                  </button>
                </div>
              </div>
            </div>

          </mat-card-content>
        </mat-card>
      </div>

      <div class="step-actions">
        <button mat-button matStepperPrevious>Back</button>
        <button mat-raised-button color="primary" matStepperNext>
          Next: Generate PDF <mat-icon>picture_as_pdf</mat-icon>
        </button>
      </div>
    </mat-step>

    <!-- ═══════════════════════════════════════════════════════
         STEP 6 — Generate PDF
    ═══════════════════════════════════════════════════════ -->
    <mat-step label="Generate PDF">
      <h3 class="step-title">Generate Impact Report PDF</h3>
      <div class="help-note">
        <mat-icon>info</mat-icon>
        <ul>
          <li>Switch off any section you don’t want under <strong>Sections to include</strong>, and adjust wording under <strong>Headings &amp; Labels</strong> if needed.</li>
          <li>Click <strong>Generate Report PDF</strong>, check the preview, then use <strong>Print / Save as PDF</strong> (choose “Save as PDF” in the print dialog).</li>
          <li><strong>Save Draft</strong> stores your progress; reopen it later with Edit on the report list.</li>
        </ul>
      </div>

      <mat-card class="summary-card">
        <mat-card-content>
          <div class="summary-row"><strong>Centre:</strong> {{configForm.value.centreName || configForm.value.centreCode}}</div>
          <div class="summary-row"><strong>Period:</strong> {{period}}</div>
          <div class="summary-row"><strong>Donor:</strong> {{configForm.value.donorName}}</div>
          <div class="summary-row"><strong>Case Studies:</strong> {{selectedChildren.length}} children selected</div>
          <div class="summary-row"><strong>Volunteer Events:</strong> {{events.length}} event(s)</div>
          <div class="summary-row"><strong>Counselling Photos:</strong> {{counsellingPhotoUrls.length}}</div>
          <div class="summary-row"><strong>Teaching Photos:</strong> {{teachingPhotoUrls.length}}</div>
        </mat-card-content>
      </mat-card>

      <mat-card class="summary-card">
        <mat-card-title>Sections to include in the PDF</mat-card-title>
        <mat-card-content>
          <div class="section-toggles">
            <mat-slide-toggle *ngFor="let opt of sectionOptions" color="primary" [(ngModel)]="sections[opt.key]">
              {{opt.label}}
            </mat-slide-toggle>
          </div>
        </mat-card-content>
      </mat-card>

      <mat-expansion-panel class="summary-card">
        <mat-expansion-panel-header>
          <mat-panel-title><mat-icon>title</mat-icon>&nbsp;Headings &amp; Labels</mat-panel-title>
          <mat-panel-description>Report title, section headings, gallery and case-study headings</mat-panel-description>
        </mat-expansion-panel-header>
        <p class="photo-count">
          Use <strong>{{'{'}}centre{{'}'}}</strong> for the centre name, <strong>{{'{'}}period{{'}'}}</strong> for the
          report period and, in case-study headings, <strong>{{'{'}}name{{'}'}}</strong> for the child's name.
          Clear a field to go back to the default.
        </p>
        <div class="headings-grid">
          <mat-form-field appearance="outline" *ngFor="let f of headingFields">
            <mat-label>{{f.label}}</mat-label>
            <input matInput [(ngModel)]="headings[f.key]">
          </mat-form-field>
        </div>
      </mat-expansion-panel>

      <div class="generate-btn-area">
        <div class="generate-row">
          <button mat-raised-button color="primary" (click)="generateAndPrint()"
            [disabled]="generatingPdf || saving" class="generate-pdf-btn">
            <mat-icon>{{generatingPdf ? 'hourglass_empty' : 'picture_as_pdf'}}</mat-icon>
            {{generatingPdf ? 'Generating…' : 'Generate Report PDF'}}
          </button>

          <button mat-stroked-button color="accent" (click)="saveReport()"
            [disabled]="saving || generatingPdf" class="save-draft-btn">
            <mat-icon>{{saving ? 'hourglass_empty' : 'save'}}</mat-icon>
            {{saving ? 'Saving…' : 'Save Draft'}}
          </button>
        </div>

        <p class="saved-status" *ngIf="savedAt">
          <mat-icon>check_circle</mat-icon>
          Last saved: {{savedAt | date:'d MMM yyyy, h:mm a'}}
          <span *ngIf="reportId" class="report-id">· ID: {{reportId}}</span>
        </p>

        <mat-progress-bar *ngIf="generatingPdf" mode="indeterminate" style="width:320px;margin-top:8px;"></mat-progress-bar>
        <p *ngIf="generatingPdf" style="font-size:12px;color:#888;margin-top:4px;">
          Assembling report — preview will appear below when ready.
        </p>

        <button mat-button (click)="buildAndShowPrompt()" class="view-prompt-btn">
          <mat-icon>code</mat-icon> View / Copy Raw Prompt
        </button>
      </div>

      <!-- ── Inline Preview ── -->
      <div *ngIf="previewHtml" class="preview-wrapper">
        <div class="preview-toolbar">
          <mat-icon class="preview-icon">preview</mat-icon>
          <span class="preview-label">Report Preview — check page breaks before printing</span>
          <div class="preview-actions">
            <button mat-raised-button color="primary" (click)="printReport()">
              <mat-icon>print</mat-icon> Print / Save as PDF
            </button>
            <button mat-icon-button matTooltip="Regenerate" (click)="previewHtml = ''; generateAndPrint()">
              <mat-icon>refresh</mat-icon>
            </button>
            <button mat-icon-button matTooltip="Close preview" (click)="previewHtml = ''">
              <mat-icon>close</mat-icon>
            </button>
          </div>
        </div>
        <iframe id="reportPreviewFrame" class="report-preview-frame"></iframe>
        <div class="preview-footer">
          Press <strong>Ctrl+P</strong> (or <strong>⌘+P</strong> on Mac) → <strong>Save as PDF</strong> in the print dialog
        </div>
      </div>

      <!-- ── AI Prompt Panel ── -->
      <div *ngIf="showAiPromptPanel" id="ai-prompt-panel" class="ai-prompt-panel">
        <div class="ai-prompt-header">
          <div class="ai-prompt-header-left">
            <mat-icon class="ai-prompt-icon">smart_toy</mat-icon>
            <div>
              <p class="ai-prompt-title">Your AI Report Prompt is Ready</p>
              <p class="ai-prompt-subtitle">Copy this prompt and paste it into Claude, ChatGPT, or Gemini</p>
            </div>
          </div>
          <div class="ai-prompt-actions">
            <button mat-raised-button color="accent" (click)="copyPrompt()">
              <mat-icon>content_copy</mat-icon> Copy Prompt
            </button>
            <button mat-icon-button (click)="showAiPromptPanel = false" matTooltip="Close">
              <mat-icon>close</mat-icon>
            </button>
          </div>
        </div>

        <div class="ai-prompt-steps">
          <div class="ai-prompt-step">
            <span class="step-badge">1</span>
            <span>Click <strong>Copy Prompt</strong> above</span>
          </div>
          <div class="ai-prompt-step">
            <span class="step-badge">2</span>
            <span>Open <a href="https://claude.ai" target="_blank">claude.ai</a>, ChatGPT, or Gemini</span>
          </div>
          <div class="ai-prompt-step">
            <span class="step-badge">3</span>
            <span>Paste and send — the AI will generate the complete Word document code</span>
          </div>
          <div class="ai-prompt-step">
            <span class="step-badge">4</span>
            <span>Run the Python code it gives you, or save the HTML it generates and print to PDF</span>
          </div>
        </div>

        <textarea class="ai-prompt-textarea" readonly [value]="aiPrompt" rows="22"></textarea>
      </div>

      <div class="step-actions">
        <button mat-button matStepperPrevious>Back</button>
      </div>
    </mat-step>

  </mat-stepper>
</div>
